import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ValidationPreviewTable from './ValidationPreviewTable';
import { bulkUploadProducts, importBulkRows, validateBulkUploadRow, bulkUpdateProducts, searchVendors, downloadBulkUploadTemplate } from '../../services/shipturtleApi';
import { productsPath, useProductsChildRoute } from './productsNavigation';
import './ManageProducts.css';

const NUMERIC_FIELDS = [
  'price', 'compare_price', 'compare_at_price',
  'weight_kg', 'length_cm', 'width_cm', 'height_cm',
  'gst_rate', 'brand_commission', 'commission_pct',
  'inventory', 'inventory_quantity', 'merchant_split'
];

const sanitizeNumericField = (value) => {
  if (value === null || value === undefined || value === '') return value;
  // Strip currency symbols, spaces, commas — keep digits, dot, minus
  const cleaned = String(value).trim().replace(/[^0-9.\-]/g, '');
  return cleaned === '' ? value : cleaned;
};

const sanitizeRowNumericFields = (row) => {
  if (!row) return row;
  const sanitized = { ...row };
  NUMERIC_FIELDS.forEach(field => {
    if (sanitized[field] !== undefined) {
      sanitized[field] = sanitizeNumericField(sanitized[field]);
    }
  });
  return sanitized;
};

// Two different things are called "status" in this flow:
//   * the spreadsheet's `status` column  -> the product's storefront catalog status
//     (`active`/`draft`), which the backend applies on import/update;
//   * a row's validation verdict         -> `valid`/`warning`/`error`, which drives the
//     preview table's badges, filters and counts.
//
// A validation row carries the verdict on `status` (the table reads `row.status`), so the
// sheet's catalog status is parked here instead of being overwritten, and is put back on
// `status` only when building the payload sent to the backend.
const CATALOG_STATUS_KEY = 'catalog_status';

// Keys that exist only to drive the preview UI. They are stripped before sending, so the
// backend receives something with the same shape as a spreadsheet row.
const VALIDATION_ONLY_KEYS = [
  CATALOG_STATUS_KEY,
  'errors', 'warnings', 'errorMessage', 'warningMessage', 'row_number'
];

// Convert a preview/validation row back into a sheet-shaped row for the backend:
// restore the catalog status onto `status` and drop the UI-only bookkeeping.
const toBackendRow = (row) => {
  if (!row) return row;
  const payload = { ...row };
  const catalogStatus = payload[CATALOG_STATUS_KEY];

  VALIDATION_ONLY_KEYS.forEach(key => { delete payload[key]; });

  // Restore the sheet's value. A blank/absent catalog status stays absent so the backend
  // applies its own default (draft) rather than receiving the validation verdict.
  if (catalogStatus !== undefined && catalogStatus !== null && String(catalogStatus).trim() !== '') {
    payload.status = catalogStatus;
  } else {
    delete payload.status;
  }

  return payload;
};

function BulkAddProductsPage() {
  const navigate = useNavigate();
  const { closeChild: closeUpload } = useProductsChildRoute('add');
  const [currentStep, setCurrentStep] = useState('upload'); // 'upload', 'preview', 'processing', 'complete'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [validationResults, setValidationResults] = useState([]);
  const [validationSummary, setValidationSummary] = useState(null); // For large batches (>1000 rows)
  const [isProcessing, setIsProcessing] = useState(false);
  const [jobProgress, setJobProgress] = useState(null);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState('vendor');
  const [uploadMode, setUploadMode] = useState('add'); // 'add' or 'update'
  const [selectedVendor, setSelectedVendor] = useState('');
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [selectedVendorDetails, setSelectedVendorDetails] = useState(null);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const vendorDropdownRef = useRef(null);
  const vendorSearchTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch user role on mount
  React.useEffect(() => {
    async function fetchRole() {
      try {
        const res = await import('../../services/shipturtleApi');
        const roleData = await res.getCurrentUserRole();
        setUserRole(roleData.role || 'vendor');
      } catch (e) {
        setUserRole('vendor');
      }
    }
    fetchRole();
  }, []);

  const performVendorSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setVendorSuggestions([]);
      setLoadingVendors(false);
      return;
    }

    try {
      setLoadingVendors(true);
      const response = await searchVendors(searchTerm.trim(), 20);
      const results = response.vendors || response.results || [];
      const normalizedSearch = searchTerm.trim().toLowerCase();

      const filteredResults = results.filter((vendor) => {
        const displayName = (vendor.brand_name || vendor.company_name || '').toLowerCase();
        return displayName.includes(normalizedSearch);
      });

      const sortedResults = filteredResults.sort((a, b) => {
        const aName = (a.brand_name || a.company_name || '').toLowerCase();
        const bName = (b.brand_name || b.company_name || '').toLowerCase();
        const aStarts = aName.startsWith(normalizedSearch);
        const bStarts = bName.startsWith(normalizedSearch);

        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return aName.localeCompare(bName);
      });

      setVendorSuggestions(sortedResults);
    } catch (searchError) {
      console.error('Error searching vendors:', searchError);
      setVendorSuggestions([]);
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  const handleVendorSearchChange = useCallback((event) => {
    const value = event.target.value;
    setVendorSearchTerm(value);
    setShowVendorDropdown(true);

    if (vendorSearchTimeoutRef.current) {
      clearTimeout(vendorSearchTimeoutRef.current);
    }

    if (!value || value.trim().length === 0) {
      setSelectedVendor('');
      setSelectedVendorDetails(null);
      setVendorSuggestions([]);
      setLoadingVendors(false);
      return;
    }

    if (value.trim().length >= 2) {
      setLoadingVendors(true);
      vendorSearchTimeoutRef.current = setTimeout(() => {
        performVendorSearch(value);
      }, 300);
    } else {
      setVendorSuggestions([]);
      setLoadingVendors(false);
    }
  }, [performVendorSearch]);

  const handleVendorSelect = useCallback((vendor) => {
    if (!vendor) return;
    const vendorDisplayName = vendor.brand_name || vendor.company_name || '';
    setSelectedVendor(vendor.id || '');
    setSelectedVendorDetails(vendor);
    setVendorSearchTerm(vendorDisplayName);
    setVendorSuggestions([]);
    setShowVendorDropdown(false);
  }, []);

  const highlightMatch = (text, searchTerm) => {
    if (!text || !searchTerm || searchTerm.trim().length < 2) return text;
    const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearch})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => (
      part.toLowerCase() === searchTerm.trim().toLowerCase() ? <mark key={index}>{part}</mark> : part
    ));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (vendorSearchTimeoutRef.current) {
        clearTimeout(vendorSearchTimeoutRef.current);
      }
    };
  }, []);

  // Download the bulk-upload template.
  //
  // Built server-side and served as .xlsx rather than assembled here as CSV: the
  // `status` column carries a real Excel dropdown (data validation), which CSV
  // cannot express. Column order and the example row live with the parser that
  // reads them back, so the two can't drift.
  const handleDownloadTemplate = async () => {
    try {
      const { blob, filename } = await downloadBulkUploadTemplate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading template:', err);
      setError(err.message || 'Could not download the template. Please try again.');
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const validExtensions = ['csv', 'xls', 'xlsx'];

      if (!validExtensions.includes(fileExtension)) {
        setError('Invalid file type. Please upload a CSV or Excel file.');
        return;
      }

      setUploadedFile(file);
      setError(null);
    }
  };

  // Handle file upload and validation
  const handleUpload = async () => {
    if (!uploadedFile) {
      setError('Please select a file to upload.');
      return;
    }

    // Validate vendor selection for admin
    if (userRole === 'admin' && !selectedVendor) {
      setError('Please select a vendor before uploading.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', uploadedFile);

      // Add vendor_id for admin uploads
      const options = {
        validate_only: true,
        mode: uploadMode // Pass the current mode ('add' or 'update')
      };

      if (userRole === 'admin' && selectedVendor) {
        options.vendor_id = selectedVendor;
      }

      // Call API with validate_only=true and mode parameter to get validation results
      const response = await bulkUploadProducts(formData, options);

      console.log('Backend response:', response);

      // Transform backend response to match our validation results format
      // Backend returns: { message, results: { validation_results: [...], validation_summary: {...} } }
      if (response.results && Array.isArray(response.results.validation_results)) {
        // Check if this is a large batch with limited results
        const summary = response.results.validation_summary || null;

        // Get selected vendor info for admin users
        let vendorInfo = null;
        if (userRole === 'admin' && selectedVendor) {
          vendorInfo = {
            vendor_id: selectedVendor,
            vendor_name: selectedVendorDetails?.brand_name || selectedVendorDetails?.company_name || ''
          };
        }

        const validationResults = response.results.validation_results.map(result => {
          // Determine status based on errors
          let status = 'valid';
          const hasErrors = result.errors && Object.keys(result.errors).length > 0;
          const hasWarnings = result.warnings && result.warnings.length > 0;

          if (hasErrors) {
            status = 'error';
          } else if (hasWarnings) {
            status = 'warning';
          }

          return {
            ...result.data,
            row_number: result.row_number,
            // Keep the sheet's catalog status before `status` is taken over by the
            // validation verdict below — it is the value the backend applies on import.
            [CATALOG_STATUS_KEY]: result.data?.status,
            status: status,
            errors: result.errors || {},
            warnings: result.warnings || [],
            errorMessage: hasErrors
              ? Object.values(result.errors).join(', ')
              : '',
            warningMessage: hasWarnings
              ? result.warnings.join(', ')
              : '',
            // Add vendor info for admin users
            ...(vendorInfo && {
              vendor_id: vendorInfo.vendor_id,
              vendor_name: vendorInfo.vendor_name
            })
          };
        });

        setValidationResults(validationResults);
        setValidationSummary(summary); // Store summary for large batches
        setCurrentStep('preview');
      } else {
        console.error('Unexpected response format:', response);
        setError('Unexpected response format from server.');
      }

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload and validate file. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle updating a row in the preview
  const handleUpdateRow = async (rowIndex, field, newValue) => {
    const updatedResults = [...validationResults];
    updatedResults[rowIndex][field] = newValue;

    // Call backend API to re-validate the row
    try {
      const row = updatedResults[rowIndex];

      // Prepare row data for validation (remove UI-specific fields)
      const rowData = {
        title: row.title,
        sku: row.sku,
        description: row.description,
        price: row.price,
        compare_price: row.compare_price,
        compare_at_price: row.compare_at_price,
        weight_kg: row.weight_kg,
        length_cm: row.length_cm,
        width_cm: row.width_cm,
        height_cm: row.height_cm,
        hs_code: row.hs_code,
        gst_rate: row.gst_rate,
        // Send as brand_commission (CSV column name) - backend will map to commission_pct
        brand_commission: row.brand_commission || row.commission_pct,
        inventory_quantity: row.inventory_quantity || row.inventory,
        inventory: row.inventory,
        image_urls: row.image_urls,
        vendor_remarks: row.vendor_remarks,
        vendor_id: row.vendor_id,
        vendor_name: row.vendor_name,
        features: row.features,
        care_instructions: row.care_instructions,
        merchant_split: row.merchant_split,
        tags: row.tags,
        has_variants: row.has_variants,
        product_variant: row.product_variant,
        variant_values: row.variant_values,
        // Include product_id and variant_id for update mode validation
        product_id: row.product_id,
        variant_id: row.variant_id,
        // The sheet's catalog status (active/draft), NOT the row's validation verdict —
        // sent so the row is validated exactly as it will later be submitted.
        status: row[CATALOG_STATUS_KEY],
        // Include Option Value fields
        'Option 1 Value': row['Option 1 Value'],
        'Option 2 Value': row['Option 2 Value'],
        'Option 3 Value': row['Option 3 Value']
      };

      // Only include Option Name fields in add mode (not in update mode)
      if (uploadMode !== 'update') {
        rowData['Option 1 Name'] = row['Option 1 Name'];
        rowData['Option 2 Name'] = row['Option 2 Name'];
        rowData['Option 3 Name'] = row['Option 3 Name'];
      }

      // Add any meta fields
      Object.keys(row).forEach(key => {
        if (key.startsWith('meta:')) {
          rowData[key] = row[key];
        }
      });

      const validationResponse = await validateBulkUploadRow(sanitizeRowNumericFields(rowData), uploadMode);

      console.log('Validation response:', validationResponse);

      // Backend returns { validation_result: { errors, warnings, status, data } }
      const validationResult = validationResponse.validation_result || validationResponse;

      // Update row with validation results
      updatedResults[rowIndex].errors = validationResult.errors || {};
      updatedResults[rowIndex].warnings = validationResult.warnings || [];

      // Determine status
      const hasErrors = validationResult.errors && Object.keys(validationResult.errors).length > 0;
      const hasWarnings = validationResult.warnings && validationResult.warnings.length > 0;

      if (hasErrors) {
        updatedResults[rowIndex].status = 'error';
      } else if (hasWarnings) {
        updatedResults[rowIndex].status = 'warning';
      } else {
        updatedResults[rowIndex].status = 'valid';
      }

      updatedResults[rowIndex].errorMessage = hasErrors
        ? Object.values(validationResult.errors).join(', ')
        : '';
      updatedResults[rowIndex].warningMessage = hasWarnings
        ? validationResult.warnings.join(', ')
        : '';

    } catch (err) {
      console.error('Row validation error:', err);
      // Fallback to showing error
      updatedResults[rowIndex].errors = { validation: 'Failed to validate row' };
      updatedResults[rowIndex].status = 'error';
    }

    setValidationResults(updatedResults);
  };

  // Handle removing a row
  const handleRemoveRow = (rowIndex) => {
    const updatedResults = validationResults.filter((_, index) => index !== rowIndex);
    setValidationResults(updatedResults);
  };

  // Validate a single row
  const _validateRow = (row) => {
    const errors = {};

    if (!row.title || row.title.trim() === '') {
      errors.title = 'Title is required';
    }

    if (!row.sku || row.sku.trim() === '') {
      errors.sku = 'SKU is required';
    } else if (row.sku.length < 3 || row.sku.length > 50) {
      errors.sku = 'SKU must be between 3 and 50 characters';
    }

    if (!row.price || isNaN(row.price) || parseFloat(row.price) <= 0) {
      errors.price = 'Valid price is required';
    }

    // Check both inventory_quantity and inventory (backend uses inventory_quantity)
    const inventoryField = row.inventory_quantity !== undefined ? row.inventory_quantity : row.inventory;
    if (inventoryField === '' || inventoryField === null || isNaN(inventoryField) || parseInt(inventoryField) < 0) {
      errors.inventory_quantity = 'Valid inventory is required';
    }

    // GST rate is required for each variant row in add mode
    if (row.gst_rate === undefined || row.gst_rate === null || row.gst_rate === '') {
      errors.gst_rate = 'GST rate is required';
    } else if (isNaN(row.gst_rate) || parseFloat(row.gst_rate) < 0 || parseFloat(row.gst_rate) > 100) {
      errors.gst_rate = 'GST rate must be between 0 and 100';
    }

    return errors;
  };

  // Handle re-upload
  const handleReUpload = () => {
    setUploadedFile(null);
    setValidationResults([]);
    setValidationSummary(null);
    setCurrentStep('upload');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle cancel - return to AddProductsPage
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      closeUpload();
    }
  };

  // Handle confirm import
  const handleConfirmImport = async () => {
    // Filter out rows with errors and sanitize numeric fields before sending to backend.
    // Order matters: the filter reads the validation verdict on `status`, and only then
    // does toBackendRow swap that key back to the sheet's catalog status for the backend.
    const validRows = validationResults
      .filter(row => row.status === 'valid' || row.status === 'warning')
      .map(sanitizeRowNumericFields)
      .map(toBackendRow);

    // For large batches, check if we have any importable rows
    const importableCount = validationSummary
      ? (validationSummary.valid_count + validationSummary.warning_count)
      : validRows.length;

    if (importableCount === 0) {
      setError('No valid rows to import. Please fix errors or upload a new file.');
      return;
    }

    // For large batches, warn user that only displayed rows will be imported
    if (validationSummary && validRows.length < importableCount) {
      const confirmed = window.confirm(
        `Warning: You have ${importableCount.toLocaleString()} importable rows (${validationSummary.valid_count.toLocaleString()} valid + ${validationSummary.warning_count.toLocaleString()} warnings), ` +
        `but only ${validRows.length.toLocaleString()} are displayed in the table. ` +
        `Only the displayed rows will be imported. Do you want to continue?`
      );
      if (!confirmed) {
        return;
      }
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Determine if this is an add or update operation
      if (uploadMode === 'update') {
        const response = await bulkUpdateProducts(validRows);
        const updatedCount = response.updated || 0;

        setCurrentStep('complete');
        setJobProgress({
          totalRows: validRows.length,
          processedRows: validRows.length,
          successCount: updatedCount,
          failureCount: response.errors?.length || 0,
          status: 'completed',
          errors: response.errors || [],
          pendingApproval: true
        });
      } else {
        // Bulk add: call the import API as before.
        // Catalog status is per-product, from the sheet's `status` column. Approval
        // state is not caller-controlled — the backend always imports as Pending.
        const options = {};

        // Add vendor_id for admin uploads
        if (userRole === 'admin' && selectedVendor) {
          options.vendor_id = selectedVendor;
        }

        const response = await importBulkRows(validRows, options);

        // For large imports (>= 1000 rows), show job progress
        if (validRows.length >= 1000) {
          setCurrentStep('processing');
          setJobProgress({
            jobId: response.job_id || 'JOB-' + Date.now(),
            totalRows: validRows.length,
            processedRows: 0,
            status: 'processing'
          });

          // Simulate job progress updates (replace with actual polling later)
          simulateJobProgress(validRows.length);
        } else {
          // For small imports, show immediate success
          setCurrentStep('complete');
          setJobProgress({
            totalRows: validRows.length,
            processedRows: validRows.length,
            successCount: response.imported_count || validRows.length,
            failureCount: response.failed_count || 0,
            status: 'completed',
            errors: response.errors || []
          });
        }
      }

    } catch (err) {
      console.error('Import/Update error:', err);
      setError(err.message || `Failed to ${uploadMode === 'update' ? 'update' : 'import'} products. Please try again.`);
      setIsProcessing(false);
    }
  };

  // Simulate job progress for large imports
  const simulateJobProgress = (totalRows) => {
    let processed = 0;
    const interval = setInterval(() => {
      processed += Math.floor(Math.random() * 50) + 10;

      if (processed >= totalRows) {
        processed = totalRows;
        clearInterval(interval);
        setJobProgress({
          totalRows,
          processedRows: totalRows,
          successCount: Math.floor(totalRows * 0.95),
          failureCount: Math.floor(totalRows * 0.05),
          status: 'completed'
        });
        setCurrentStep('complete');
        setIsProcessing(false);
      } else {
        setJobProgress(prev => ({
          ...prev,
          processedRows: processed
        }));
      }
    }, 500);
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'upload':
        return renderUploadStep();
      case 'preview':
        return renderPreviewStep();
      case 'processing':
        return renderProcessingStep();
      case 'complete':
        return renderCompleteStep();
      default:
        return renderUploadStep();
    }
  };

  const renderUploadStep = () => (
    <div className="uploadStep">
      <div className="stepHeader">
        <h2>Step 1: Upload Product File</h2>
        <p>Upload a CSV or Excel file containing your product data</p>
      </div>

      {/* Vendor Selector for Admin */}
      {userRole === 'admin' && (
        <div className="vendorSelectorSection" style={{
          marginBottom: '24px',
          padding: '16px',
          background: '#f0f9ff',
          borderRadius: '8px',
          border: '2px solid #3b82f6'
        }}>
          <label style={{
            display: 'block',
            fontWeight: '600',
            marginBottom: '8px',
            color: '#1e40af'
          }}>
            Select Vendor *
          </label>
          <div className="searchableDropdown" ref={vendorDropdownRef}>
            <input
              type="text"
              value={vendorSearchTerm}
              onChange={handleVendorSearchChange}
              onFocus={() => {
                setShowVendorDropdown(true);
                if (vendorSearchTerm.trim().length >= 2) {
                  performVendorSearch(vendorSearchTerm);
                }
              }}
              placeholder="Search vendors..."
              className="formInput searchInputNarrow"
              autoComplete="off"
            />
            {showVendorDropdown && (
              <div className="dropdownMenu">
                {loadingVendors ? (
                  <div className="dropdownItem dropdownItemDisabled">
                    Searching...
                  </div>
                ) : vendorSuggestions.length > 0 ? (
                  vendorSuggestions.map((vendor) => (
                    <div
                      key={vendor.id}
                      className="dropdownItem"
                      onClick={() => handleVendorSelect(vendor)}
                    >
                      <div>{highlightMatch(vendor.brand_name || vendor.company_name, vendorSearchTerm)}</div>
                      {vendor.email && (
                        <small style={{ color: '#6b7280' }}>{vendor.email}</small>
                      )}
                    </div>
                  ))
                ) : vendorSearchTerm.trim().length >= 2 ? (
                  <div className="dropdownItem dropdownItemDisabled">
                    No vendors found
                  </div>
                ) : (
                  <div className="dropdownItem dropdownItemDisabled">
                    Type at least 2 characters to search
                  </div>
                )}
              </div>
            )}
          </div>
          <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
            Products will be uploaded for the selected vendor
          </p>
        </div>
      )}

      <div className="uploadModeSelector" style={{
        marginBottom: '24px',
        padding: '16px',
        background: '#f8f9fa',
        borderRadius: '8px',
        display: 'flex',
        gap: '12px',
        alignItems: 'center'
      }}>
        <label style={{ fontWeight: '600', marginRight: '8px' }}>Upload Mode:</label>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '6px' }}>
          <input
            type="radio"
            value="add"
            checked={uploadMode === 'add'}
            onChange={(e) => setUploadMode(e.target.value)}
          />
          <span>Bulk Add (New Products)</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '6px' }}>
          <input
            type="radio"
            value="update"
            checked={uploadMode === 'update'}
            onChange={(e) => setUploadMode(e.target.value)}
          />
          <span>Bulk Update (Existing Products)</span>
        </label>
      </div>

      {uploadMode === 'add' && (
        <div className="downloadTemplateSection">
          <div className="templateInfo">
            <h3>Don't have a template?</h3>
            <p>
              Download our pre-formatted Excel template with all required columns, example
              data, and a status dropdown on each row.
            </p>
          </div>
          <button onClick={handleDownloadTemplate} className="downloadTemplateButton">
            📥 Download Template
          </button>
        </div>
      )}

      {uploadMode === 'update' && (
        <>
          <div className="downloadTemplateSection" style={{ background: '#fff3cd', borderColor: '#ffc107' }}>
            <div className="templateInfo">
              <h3>⚠️ Update Mode</h3>
              <p style={{ margin: 0 }}>
                Please upload only the products you want to change. Uploading a file that contains
                many products may overwrite fields unintentionally. Use the export from Manage
                Products to limit rows to the exact products you want to update.
              </p>
            </div>
          </div>

          <div className="downloadTemplateSection" style={{ background: '#e7f3ff', borderColor: '#2196F3' }}>
            <div className="templateInfo">
              <h3>💡 For Bulk Update</h3>
              <p>Export products from the Manage Products page, edit the CSV, and upload it here to update existing products.</p>
            </div>
          </div>
        </>
      )}

      <div className="uploadSection">
        <div className="fileInputWrapper">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            onChange={handleFileSelect}
            className="fileInput"
            id="bulkUploadInput"
          />
          <label htmlFor="bulkUploadInput" className="fileInputLabel">
            {uploadedFile ? (
              <>
                <span className="fileName">📄 {uploadedFile.name}</span>
                <span className="fileSize">({(uploadedFile.size / 1024).toFixed(2)} KB)</span>
              </>
            ) : (
              <>
                <span className="uploadIcon">📤</span>
                <span className="uploadText">Click to select file or drag and drop</span>
                <span className="uploadHint">Supports CSV, XLS, XLSX</span>
              </>
            )}
          </label>
        </div>

        {uploadedFile && (
          <div className="uploadActions">
            <button onClick={handleReUpload} className="changeFileButton">
              Change File
            </button>
            <button
              onClick={handleUpload}
              className="uploadButton"
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Upload & Validate'}
            </button>
          </div>
        )}
      </div>

      {isProcessing && (
        <div className="processingIndicator">
          <div className="spinner"></div>
          <p>Validating your file... This may take a moment.</p>
        </div>
      )}
    </div>
  );

  const renderPreviewStep = () => {
    // Use summary counts if available (large batch), otherwise calculate from results
    const totalRows = validationSummary?.total_rows ?? validationResults.length;
    const errorCount = validationSummary?.error_count ?? validationResults.filter(r => r.status === 'error').length;
    const warningCount = validationSummary?.warning_count ?? validationResults.filter(r => r.status === 'warning').length;
    const validCount = validationSummary?.valid_count ?? validationResults.filter(r => r.status === 'valid').length;
    const displayedResultsCount = validationResults.length;

    // Calculate valid rows for import (valid + warnings)
    const importableCount = validCount + warningCount;

    return (
      <div className="previewStep">
        <div className="stepHeader">
          <h2>Step 2: Review & Fix Errors</h2>
          <p>Review the validation results and fix any errors before importing</p>
        </div>

        {/* Show summary banner for large batches */}
        {validationSummary && (
          <div className="largeBatchWarning" style={{
            background: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            color: '#856404'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '24px', flexShrink: 0 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#856404', fontSize: '16px', fontWeight: '600' }}>
                  Large Batch Detected ({totalRows.toLocaleString()} rows)
                </h4>
                <p style={{ margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5' }}>
                  Showing first {displayedResultsCount} rows with errors/warnings to prevent browser crashes.
                </p>
                <div style={{
                  background: 'white',
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                  padding: '12px',
                  marginTop: '12px'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', fontSize: '14px' }}>
                    <div>
                      <strong>Total Rows:</strong> {totalRows.toLocaleString()}
                    </div>
                    <div style={{ color: '#dc2626' }}>
                      <strong>Errors:</strong> {errorCount.toLocaleString()}
                      {errorCount > displayedResultsCount && (
                        <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                          (showing first {Math.min(displayedResultsCount, errorCount).toLocaleString()})
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#f59e0b' }}>
                      <strong>Warnings:</strong> {warningCount.toLocaleString()}
                      {warningCount > displayedResultsCount - Math.min(displayedResultsCount, errorCount) && (
                        <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                          (showing {Math.max(0, displayedResultsCount - Math.min(displayedResultsCount, errorCount)).toLocaleString()})
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#10b981' }}>
                      <strong>Valid:</strong> {validCount.toLocaleString()}
                    </div>
                  </div>
                  {validationSummary.note && (
                    <p style={{ margin: '12px 0 0 0', fontSize: '13px', fontStyle: 'italic', color: '#856404' }}>
                      {validationSummary.note}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <ValidationPreviewTable
          validationResults={validationResults}
          validationSummary={validationSummary}
          onUpdateRow={handleUpdateRow}
          onRemoveRow={handleRemoveRow}
          userRole={userRole}
          uploadMode={uploadMode}
        />

        {uploadMode === 'add' && (
          <div className="importConfigSection">
            <div className="configHeader">
              <h3>Import Configuration</h3>
              <p>
                All imported products are submitted for approval. Set each product&apos;s
                status (Active or Draft) in the <strong>status</strong> column of the
                template — that status applies once the product is approved. Leave it
                blank to default to Draft.
              </p>
            </div>
          </div>
        )}

        {uploadMode === 'update' && (
          <div className="importConfigSection" style={{ background: '#fff3cd', borderColor: '#ffc107' }}>
            <div className="configHeader">
              <p>All product updates will be submitted for approval before being applied.</p>
            </div>
          </div>
        )}

        <div className="previewActions">
          <button onClick={handleReUpload} className="secondaryButton">
            Re-upload File
          </button>
          <button
            onClick={handleConfirmImport}
            className="primaryButton"
            disabled={isProcessing || importableCount === 0}
            title={importableCount === 0 ? `No valid rows to ${uploadMode === 'update' ? 'update' : 'import'}` : ''}
          >
            {isProcessing
              ? (uploadMode === 'update' ? 'Updating...' : 'Importing...')
              : `Confirm ${uploadMode === 'update' ? 'Update' : 'Import'} (${importableCount.toLocaleString()} products)`}
          </button>
        </div>

        {errorCount > 0 && (
          <div className="warningMessage">
            <span className="warningIcon">⚠</span>
            <span>
              {errorCount.toLocaleString()} row(s) have errors and will not be imported.
              {validationSummary && errorCount > displayedResultsCount && (
                <span> Note: Only showing first {displayedResultsCount} error rows in the table above.</span>
              )}
              {' '}Fix the errors or remove those rows to proceed.
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderProcessingStep = () => {
    const progress = jobProgress ? (jobProgress.processedRows / jobProgress.totalRows) * 100 : 0;

    return (
      <div className="processingStep">
        <div className="processingContent">
          <div className="processingIcon">⏳</div>
          <h2>Processing Your Import</h2>
          <p>Please wait while we import your products. This may take a few minutes.</p>

          {jobProgress && (
            <div className="progressSection">
              <div className="progressBar">
                <div
                  className="progressFill"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="progressStats">
                <span>{jobProgress.processedRows} of {jobProgress.totalRows} rows processed</span>
                <span>{Math.round(progress)}%</span>
              </div>
            </div>
          )}

          <div className="processingNote">
            <p>
              <strong>Note:</strong> You can safely close this page.
              We'll send you an email notification when the import is complete.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderCompleteStep = () => {
    const isPendingApproval = jobProgress?.pendingApproval;
    return (
      <div className="completeStep">
        <div className="completeContent">
          <div className="successIcon">✓</div>
          <h2>{uploadMode === 'update' ? 'Update Complete!' : 'Import Complete!'}</h2>

          {isPendingApproval && jobProgress && (
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              color: '#15803d',
              fontWeight: 500,
              fontSize: '15px'
            }}>
              {jobProgress.successCount} product{jobProgress.successCount !== 1 ? 's' : ''} updated and moved to Pending Approval
            </div>
          )}

          {jobProgress && (
            <div className="importSummary">
              <div className="summaryCard">
                <div className="summaryItem">
                  <span className="summaryLabel">Total Rows</span>
                  <span className="summaryValue">{jobProgress.totalRows}</span>
                </div>
                <div className="summaryItem success">
                  <span className="summaryLabel">{uploadMode === 'update' ? 'Successfully Updated' : 'Successfully Imported'}</span>
                  {/* ?? not ||: a genuine zero must render as 0, not fall through to the
                      row count and claim every row succeeded when none did. */}
                  <span className="summaryValue">{jobProgress.successCount ?? jobProgress.totalRows}</span>
                </div>
                {jobProgress.failureCount > 0 && (
                  <div className="summaryItem error">
                    <span className="summaryLabel">Failed</span>
                    <span className="summaryValue">{jobProgress.failureCount}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="completeActions">
            {isPendingApproval ? (
              <button
                onClick={() => navigate(productsPath('pending'))}
                className="primaryButton"
              >
                Go to Pending Approval
              </button>
            ) : (
              <button
                onClick={() => navigate(productsPath('manage'))}
                className="primaryButton"
              >
                View Products
              </button>
            )}
            <button
              onClick={() => {
                setCurrentStep('upload');
                setUploadedFile(null);
                setValidationResults([]);
                setValidationSummary(null);
                setJobProgress(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              className="secondaryButton"
            >
              Import More Products
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bulkAddProductsContainer">
      <div className="header">
        <h1 className="title">Bulk Add Products</h1>
        <div className="headerInfo">
          <span className="infoBadge">Bulk Upload</span>
          <button onClick={handleCancel} className="cancelButton">Cancel</button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="errorMessage" style={{
          background: '#fee',
          border: '2px solid #dc2626',
          borderRadius: '8px',
          padding: '16px 20px',
          margin: '20px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          boxShadow: '0 2px 8px rgba(220, 38, 38, 0.1)'
        }}>
          <span className="errorIcon" style={{
            fontSize: '24px',
            color: '#dc2626',
            fontWeight: 'bold'
          }}>⚠️</span>
          <span className="errorText" style={{
            flex: 1,
            color: '#991b1b',
            fontSize: '15px',
            fontWeight: '500'
          }}>{error}</span>
          <button onClick={() => setError(null)} className="dismissError" style={{
            background: 'transparent',
            border: 'none',
            color: '#dc2626',
            fontSize: '20px',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background 0.2s'
          }} onMouseOver={(e) => e.target.style.background = '#fecaca'} onMouseOut={(e) => e.target.style.background = 'transparent'}>✕</button>
        </div>
      )}

      {/* Progress Steps Indicator */}
      <div className="stepsIndicator">
        <div className={`stepIndicator ${currentStep === 'upload' ? 'active' : ''} ${['preview', 'processing', 'complete'].includes(currentStep) ? 'completed' : ''}`}>
          <div className="stepNumber">1</div>
          <div className="stepLabel">Upload</div>
        </div>
        <div className="stepConnector"></div>
        <div className={`stepIndicator ${currentStep === 'preview' ? 'active' : ''} ${['processing', 'complete'].includes(currentStep) ? 'completed' : ''}`}>
          <div className="stepNumber">2</div>
          <div className="stepLabel">Preview</div>
        </div>
        <div className="stepConnector"></div>
        <div className={`stepIndicator ${currentStep === 'processing' ? 'active' : ''} ${currentStep === 'complete' ? 'completed' : ''}`}>
          <div className="stepNumber">3</div>
          <div className="stepLabel">Import</div>
        </div>
        <div className="stepConnector"></div>
        <div className={`stepIndicator ${currentStep === 'complete' ? 'active completed' : ''}`}>
          <div className="stepNumber">4</div>
          <div className="stepLabel">Complete</div>
        </div>
      </div>

      {/* Step Content */}
      <div className="stepContent">
        {renderStepContent()}
      </div>
    </div>
  );
}

// Generate mock validation results for testing
function _generateMockValidationResults(count) {
  const results = [];
  const statuses = ['valid', 'valid', 'valid', 'warning', 'error']; // More valid than errors
  const categories = ['electronics', 'clothing', 'home-garden', 'sports', 'books'];

  for (let i = 0; i < count; i++) {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const hasError = status === 'error';
    const hasWarning = status === 'warning';

    const errors = {};
    let errorMessage = '';

    if (hasError) {
      // Add random errors
      if (Math.random() > 0.5) errors.sku = 'SKU already exists';
      if (Math.random() > 0.5) errors.price = 'Invalid price format';
      if (Math.random() > 0.7) errors.category = 'Category not found';
      errorMessage = `${Object.keys(errors).length} field(s) have errors`;
    } else if (hasWarning) {
      errorMessage = 'Missing optional field: compare_at_price';
    }

    results.push({
      sku: `SKU-${String(i + 1).padStart(4, '0')}${hasError && errors.sku ? '-INVALID' : ''}`,
      title: `Product ${i + 1}`,
      category: categories[Math.floor(Math.random() * categories.length)],
      price: hasError && errors.price ? 'invalid' : (Math.random() * 100 + 10).toFixed(2),
      inventory: Math.floor(Math.random() * 200),
      status,
      errors,
      errorMessage
    });
  }

  return results;
}

export default BulkAddProductsPage;